import React, { useEffect, useMemo, useRef, useState } from 'react'
import styles from './index.module.scss'
import { PullToRefresh, Toast, Swiper } from 'antd-mobile'
import Icon from '@/components/icon/icon'
import ProductList from '@/components/product-list/product-list'
import ResetModal, { ResetModalProps } from '@/components/reset-modal/reset-modal'
import productApi, { ProductListResponse, ProductListParams } from '@/apis/product'
import { parseNum, Storage, formatTime } from '@bihu/common-js'
import { TOP_RECOMMEND_INDEX, HOME_DIALOG_BANNER_INDEX, HOME_DIALOG_BANNER_TIME } from '@/constants/storage'
import useFirstOrAfter from '@/hooks/use-first-or-after'
import bannerApi, { HomeDialogBannerResponse } from '@/apis/banner'
import { ENTRANCE_TYPE } from '@/constants/common'
import { jumpInlinePage, reportProductExposure } from '@/utils/utils'
import { EntranceType } from '@/types/apis/index'

interface Props{}

const Index:React.FC<Props> = () => {

  // 弹窗Ref
  const retainPopupRef = useRef<ResetModalProps>(null)
  const show = () => {
    retainPopupRef && retainPopupRef.current?.show()
  }
  // 首页弹窗广告当前index
  const [homeDialogBannerIndex, setHomeDialogBannerIndex] = useState<number>(0)
  // 首页弹窗广告数组
  const [homeDialogBannerArr, setHomeDialogBannerArr] = useState<HomeDialogBannerResponse[]>([])
  // 获取首页弹窗广告
  const homeDialogBanner = async() => {
    const res = await bannerApi.homeDialogBanner()
    setHomeDialogBannerArr(res)

    // 更新首页弹窗广告当前index
    const index = Storage.get(HOME_DIALOG_BANNER_INDEX) || 0
    homeDialogBannerAdd(index, res.length)
  }

  // 弹窗产品
  const homeDialogBannerProduct = useMemo(() => {
    return homeDialogBannerArr[homeDialogBannerIndex]
  }, [homeDialogBannerArr, homeDialogBannerIndex])

  // 监听弹窗产品存在，和今天没弹窗过
  useEffect(() => {
    if (homeDialogBannerProduct && (Storage.get(HOME_DIALOG_BANNER_TIME) !== formatTime(Date.now(), 'YYYY-MM-DD'))) {
      show()

      // 记录当天首页弹窗广告时间
      Storage.set(HOME_DIALOG_BANNER_TIME, formatTime(Date.now(), 'YYYY-MM-DD'))
      // 上报产品曝光
      reportProductExposure(homeDialogBannerProduct.productId, ENTRANCE_TYPE.HOME_PAGE_POPUP)
    }
  }, [homeDialogBannerProduct])

  // 更新首页弹窗广告当前index
  const homeDialogBannerAdd = (index: number, length: number) => {

    if (Storage.get(HOME_DIALOG_BANNER_TIME) === formatTime(Date.now(), 'YYYY-MM-DD')) {
      return
    }

    if (length - 1 <= index) {
      setHomeDialogBannerIndex(0)
      Storage.set(HOME_DIALOG_BANNER_INDEX, 0)
    } else {
      setHomeDialogBannerIndex(index + 1)
      Storage.set(HOME_DIALOG_BANNER_INDEX, index + 1)
    }
  }

  // 顶部推荐产品当前index
  const [topRecommendIndex, setTopRecommendIndex] = useState<number>(0)
  // 顶部推荐产品数组
  const [topRecommendArr, setTopRecommendArr] = useState<ProductListResponse[]>([])
  // 获取顶部推荐产品
  const topRecommend = async() => {
    const res = await productApi.topRecommend()
    setTopRecommendArr(res)

    // 更新顶部推荐产品当前index
    const index = Storage.get(TOP_RECOMMEND_INDEX) || 0
    topRecommendAdd(index, res.length)
  }

  // 顶部推荐产品
  const topRecommendProduct = useMemo(() => {
    return topRecommendArr[topRecommendIndex]
  }, [topRecommendArr, topRecommendIndex])

  useEffect(() => {
    if (topRecommendProduct) {
      // 上报产品曝光
      reportProductExposure(topRecommendProduct.id, ENTRANCE_TYPE.TOP_RECOMMENDATIONS)
    }
  }, [topRecommendProduct])


  // 更新顶部推荐产品当前index
  const topRecommendAdd = (index: number, length: number) => {
    if (length - 1 <= index) {
      setTopRecommendIndex(0)
      Storage.set(TOP_RECOMMEND_INDEX, 0)
    } else {
      setTopRecommendIndex(index + 1)
      Storage.set(TOP_RECOMMEND_INDEX, index + 1)
    }
  }


  // 中插图片数组
  const [homeCarouselBannerArr, setHomeCarouselBannerArr] = useState<HomeDialogBannerResponse[]>()
  // 获取首页中插广告
  const homeCarouselBanner = async() => {
    const res = await bannerApi.homeCarouselBanner()
    setHomeCarouselBannerArr(res)
  }
  // 中插图片切换时触发
  const onIndexChange = (index:number) => {
    // 上报产品曝光
    homeCarouselBannerArr && reportProductExposure(homeCarouselBannerArr[index].productId, ENTRANCE_TYPE.HOME_PAGE_INSERTION)
  }

  // 下拉分页标识，上次列表最后一个产品id
  const [lastProductId, setLastProductId] = useState<number>()
  // 下拉分页标识，上次列表最后一个产品权重
  const [lastShowHomePageWeight, setLastShowHomePageWeight] = useState<number>()

  // 热门推荐产品列表
  const [hotProductList, setHotProductList] = useState<ProductListResponse[]>([])
  // 获取热门推荐产品列表
  const productListFn = async(body:ProductListParams) => {

    const res = await productApi.productList({
      ...body,
      showHomePage: 1, // 是否首页热门推荐，1-热门推荐
    })

    setHotProductList(body.lastProductId ? hotProductList.concat(res) : res)

    if (res.length) {
      setLastProductId(res[res.length - 1].id)
      setLastShowHomePageWeight(res[res.length - 1].showHomePageWeight)
      setHasMore(true)
    } else {
      setHasMore(false)
    }
  }


  useFirstOrAfter(() => {
    // 获取顶部推荐产品
    topRecommend()
    // 获取首页弹窗广告
    homeDialogBanner()
    // 获取首页中插广告
    homeCarouselBanner()
    // 获取热门推荐产品列表
    productListFn({})

  }, () => {
    // eslint-disable-next-line no-console
    console.log('afterFn')
  }, [])

  // 下拉刷新回调
  const doRefresh = async() => {
    // 获取顶部推荐产品
    await topRecommend()
    // 获取首页中插广告
    homeCarouselBanner()

    // 获取热门推荐产品列表
    productListFn({})
  }

  // 是否有更多
  const [hasMore, setHasMore] = useState(true)
  // 触发更多回调
  async function loadMore() {
    await productListFn({
      lastProductId,
      lastShowHomePageWeight
    })
  }

  // 查看更多
  const lookMore = () => {
    window.location.href = '/borrow-money'
  }

  // 跳转之前判断
  const jumpAfter = (obj:HomeDialogBannerResponse, entranceType:EntranceType) => {
    // linkType: number, // 关联方式 1-关联产品 2-Banner链接
    if (obj.linkType === 1) {
      jumpInlinePage(obj.productId, entranceType, obj.title)
    } else {
      window.location.href = `/inline-page?url=${obj.bannerTargetUrl}&entranceType=${entranceType}${obj.title ? `&productName=${obj.title}` : ''}`
    }
  }

  // 顶部产品点击
  const topRecommendProductOnClick = () => {
    // 每日申请已满，0-否，1-已满
    if (topRecommendProduct.applyLimit) {
      return Toast.show('每日申请已满')
    }
    if (topRecommendProduct.showDetailPage) {
      window.location.href = `/loan-application?productId=${topRecommendProduct.id}&productName=${topRecommendProduct.productName}&entranceType=${ENTRANCE_TYPE.TOP_RECOMMENDATIONS}`
    } else {
      jumpInlinePage(topRecommendProduct.id, ENTRANCE_TYPE.TOP_RECOMMENDATIONS, topRecommendProduct.productName)
    }
  }

  return <div className={styles.indexPage}>
    <PullToRefresh
      onRefresh={doRefresh}
    >
      <div className={styles.pullToRefreshBox}>
        {
          topRecommendProduct && <div className={styles.top} onClick={topRecommendProductOnClick}>
            <div className={styles.titleBox}>
              <img src={topRecommendProduct.productLogo} alt="logo" className={styles.icon} />
              <span className={styles.name}>{topRecommendProduct.productName}</span>
              <label className={styles.text}>预估额度（元）</label>
            </div>
            <div className={styles.amount}>{parseNum(topRecommendProduct.loansLimitMax)}</div>
            <div className={styles.btn}>立即申请</div>
            <div className={styles.tip}>
              <Icon type="check-circle" className={styles.checkCircle} />
            月利率{topRecommendProduct.monthlyInterestRate}%
              <Icon type="check-circle" className={styles.checkCircle} />
            最长36期
              <Icon type="check-circle" className={styles.checkCircle} />
            最快10分钟放款
            </div>
          </div>
        }
        {
          homeCarouselBannerArr && <div className={styles.sWiperBox}>
            <Swiper
              autoplay
              loop
              style={{
                '--border-radius': '8px',
              }}
              indicator={() => null}
              onIndexChange={onIndexChange}
            >
              {
                homeCarouselBannerArr.map((item, index) => (
                  <Swiper.Item key={index}>
                    <div
                      className={styles.sWiperItem}
                      onClick={() => jumpAfter(item, ENTRANCE_TYPE.HOME_PAGE_INSERTION)}
                    >
                      <img src={item.bannerUrl} alt={item.title} className={styles.img} />
                    </div>
                  </Swiper.Item>
                ))
              }
            </Swiper>
          </div>
        }

        <div className={styles.popularRecommend} onClick={lookMore}>
          <div className={styles.title}>热门推荐</div>
          <div className={styles.more}>查看更多</div>
        </div>
        <div className={styles.productListBox}>
          <ProductList listData={hotProductList} entranceType={ENTRANCE_TYPE.HOME_PAGE_LIST} loadMore={loadMore} hasMore={hasMore} />
        </div>
      </div>
    </PullToRefresh>

    <ResetModal ref={retainPopupRef}>
      {
        homeDialogBannerProduct && <div className={styles.imgBox}
          onClick={() => jumpAfter(homeDialogBannerProduct, ENTRANCE_TYPE.HOME_PAGE_POPUP)}>
          <img src={homeDialogBannerProduct.bannerUrl} alt={homeDialogBannerProduct.title} />
        </div>
      }
    </ResetModal>
  </div>
}
export default Index
